import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Modal, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { usePWP211Mode } from './hooks/usePWP211Mode';
import { usePWP211Record } from './hooks/usePWP211Record';
import { usePWP211Navigation } from './hooks/usePWP211Navigation';
import SharedActionBar from '../../components/SharedActionBar/SharedActionBar';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import Tab2Judgment from './components/Tab2Judgment';
import './PWP211Form.css';
import { BatchAcceptanceBridge, pwp211f1AcceptanceManifest } from '../../features/acceptance';
import { pwp211Api } from '../../services/pwp211Api';

const { TabPane } = Tabs;

const PWP211Form: React.FC = () => {
  const navigate = useNavigate();
  const { mode, isEditing, startAdd, startEdit, toBrowse } = usePWP211Mode();
  const {
    record, formData, judgments, defects, basicInfos, loading, validationErrors,
    loadRecord, resetForAdd, loadSourceDraft, revertToRecord, handleSave, handleDelete,
    getField, setField, setDefects, setBasicInfos,
    addJudgmentRow, deleteJudgmentRow, updateJudgmentField,
  } = usePWP211Record();

  const loadRecordWithNav = useCallback(async (id: string) => {
    await loadRecord(id);
    return null;
  }, [loadRecord]);

  const {
    allIds, currentIdx, loadList, navFirst, navPrev, navNext, navLast,
  } = usePWP211Navigation(loadRecordWithNav);

  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAdd = () => {
    resetForAdd();
    startAdd();
  };

  const handleAcceptanceAutoFill = useCallback(() => {
    if (!record?.fp120_24) {
      Modal.info({
        title: '需要一筆有來源單號的既有外觀檢驗單',
        content: '系統會重新讀取該繳庫／託工入庫單，產生全新的未儲存草稿；不會複製或直接寫入舊資料。',
      });
      return;
    }
    const sourceNo = record.fp120_24;
    resetForAdd();
    startAdd();
    void loadSourceDraft(sourceNo);
  }, [loadSourceDraft, record, resetForAdd, startAdd]);

  const handleModify = () => {
    if (!record) return;
    startEdit();
  };

  const handleSaveClick = () => {
    handleSave(mode as 'add' | 'edit').then(saved => {
      if (saved) {
        toBrowse();
        loadList();
      }
    });
  };

  const handleCancel = () => {
    revertToRecord();
    toBrowse();
  };

  const handleDeleteClick = () => {
    if (!record) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定刪除檢驗單號 ${record.fp120_01} 嗎？`,
      okText: '確定刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          await loadList();
        }
      },
    });
  };

  /* ---- label style constants ---- */
  const lblStyle: React.CSSProperties = { width: 80, textAlign: 'right', fontSize: 13, color: '#333', flexShrink: 0 };
  const inputStyle: React.CSSProperties = { flex: 1, maxWidth: 140, padding: '3px 6px', border: '1px solid #d9d9d9', borderRadius: 3, fontSize: 13, background: '#f5f5f5', color: '#666' };
  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };

  return (
    <div className="pwp211-form">
      <BatchAcceptanceBridge
        manifest={pwp211f1AcceptanceManifest}
        records={record ? [record] : []}
        selectedKeys={record?.fp120_01 ? [record.fp120_01] : []}
        keyOf={item => item.fp120_01}
        queryExact={pwp211Api.get}
        lastApiResponse={record}
        loading={loading}
        onAutoFill={handleAcceptanceAutoFill}
        autoFillDisabled={!record}
        autoFillReason={!record ? '請先開啟一筆已有來源單號的外觀檢驗單。' : undefined}
        screenDraft={{ master: formData, judgments, defects, basic_infos: basicInfos }}
      />
      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div style={{ padding: '4px 16px', background: '#fff1f0', borderBottom: '1px solid #ffa39e' }}>
          {validationErrors.map((e, i) => (
            <span key={i} style={{ color: '#f5222d', fontSize: 12, marginRight: 8 }}>{e.message}</span>
          ))}
        </div>
      )}

      {/* Form-level fields (above tabs) -- FoxPro top=60, above pageframe top=90 */}
      <div style={{ padding: '8px 16px', background: '#fafafa', borderBottom: '1px solid #e8e8e8' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
          <div style={rowStyle}>
            <span className="form-label" data-contract-field="fp120_01" style={lblStyle}>檢驗單號</span>
            <input
              value={(getField('fp120_01') as string) || ''}
              readOnly
              disabled
              style={inputStyle} data-contract-field="fp120_01"
            />
          </div>
          <div style={rowStyle}>
            <span className="form-label" data-contract-field="fp120_11" style={lblStyle}>檢驗日期</span>
            <input
              value={(getField('fp120_11') as string) || ''}
              onChange={e => setField('fp120_11', e.target.value)}
              disabled={!isEditing}
              maxLength={8}
              style={{ ...inputStyle, background: isEditing ? '#fff' : '#f5f5f5' }} data-contract-field="fp120_11"
            />
          </div>
        </div>
        {mode === 'add' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span className="form-label" data-contract-field="fp120_24" style={lblStyle}>來源單號</span>
            <input
              value={(getField('fp120_24') as string) || ''}
              onChange={e => setField('fp120_24', e.target.value)}
              maxLength={8}
              placeholder="繳庫單／託工入庫單"
              style={{ ...inputStyle, background: '#fff' }} data-contract-field="fp120_24"
            />
            <Button
              type="primary"
              data-acceptance-action="PWP211-ACT-SOURCE-PREVIEW"
              loading={loading}
              onClick={() => loadSourceDraft((getField('fp120_24') as string) || '')}
            >
              帶入來源單
            </Button>
            <span style={{ color: '#666', fontSize: 12 }}>
              只建立草稿；儲存仍走正式驗證、權限與 Audit。
            </span>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="page-wrapper">
        {loading ? (
          <div className="loading">載入中...</div>
        ) : (
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane tab="基本資料" key="1">
              <Tab1BasicInfo
                formData={formData}
                basicInfos={basicInfos}
                isEditing={isEditing}
                getField={getField}
                setField={setField}
                setBasicInfos={setBasicInfos}
              />
            </TabPane>
            <TabPane tab="判定" key="2">
              <Tab2Judgment
                formData={formData}
                judgments={judgments}
                defects={defects}
                isEditing={isEditing}
                getField={getField}
                setField={setField}
                updateJudgmentField={updateJudgmentField}
                addJudgmentRow={addJudgmentRow}
                deleteJudgmentRow={deleteJudgmentRow}
                setDefects={setDefects}
              />
            </TabPane>
          </Tabs>
        )}
      </div>

      {/* SharedActionBar */}
      <SharedActionBar
        mode={mode}
        isEditing={isEditing}
        hasRecord={!!record}
        currentIdx={currentIdx}
        recordCount={allIds.length}
        onNavFirst={navFirst}
        onNavPrev={navPrev}
        onNavNext={navNext}
        onNavLast={navLast}
        onAdd={handleAdd}
        onModify={handleModify}
        onDelete={handleDeleteClick}
        onSave={handleSaveClick}
        onCancel={handleCancel}
        onExit={() => navigate(-1)}
        acceptanceActionIds={{
          create: 'PWP211-ACT-CREATE',
          update: 'PWP211-ACT-UPDATE',
          delete: 'PWP211-ACT-DELETE',
        }}
      />
    </div>
  );
};

export default PWP211Form;
